import { Eye, Flag, Pause, Play, Rocket, Trash2, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useScheduleErrorMessage } from '../hooks/use-schedule-error-message';
import { useRunScheduleNow, useScheduleLifecycle } from '../hooks/use-schedule-mutations';
import {
  canActivate,
  canDelete,
  canEnd,
  canPause,
  canResume,
  canRunNow,
  isWritable,
} from '../schemas/schedule-rules';

export type ScheduleDialog = 'preview' | 'activate' | 'end' | 'delete';

export interface ScheduleActionsProps {
  schedule: ProductSchedule;
  canWrite: boolean;
  onOpen: (dialog: ScheduleDialog) => void;
  /** Run now answered: show the queued run. */
  onRunQueued: (runId: string) => void;
}

/**
 * What the schedule's status allows: preview the next batch, activate a draft, pause, resume,
 * run now, end with an `onEnd` choice, delete a draft or an ended one. Activate, end and delete
 * open a dialog that says what happens first; the rest toast refusals with the schedule's copy.
 */
export function ScheduleActions({ schedule, canWrite, onOpen, onRunQueued }: ScheduleActionsProps) {
  const { t } = useTranslation('schedules');
  const message = useScheduleErrorMessage();
  const pause = useScheduleLifecycle(schedule.id, 'pause');
  const resume = useScheduleLifecycle(schedule.id, 'resume');
  const runNow = useRunScheduleNow(schedule.id);
  const pending = pause.isPending || resume.isPending || runNow.isPending;
  const fail = (action: 'pause' | 'resume' | 'runNow') => (error: unknown) =>
    toast.error(message(error, action));

  return (
    <div className="flex flex-wrap items-center gap-2" data-tour="schedules.detail.actions">
      {isWritable(schedule) ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpen('preview')}
          data-tour="schedules.detail.preview"
        >
          <Eye aria-hidden />
          {t('actions.preview')}
        </Button>
      ) : null}
      {canWrite ? (
        <>
          {canActivate(schedule) ? (
            <Button
              type="button"
              disabled={pending}
              onClick={() => onOpen('activate')}
              data-tour="schedules.detail.activate"
            >
              <Rocket aria-hidden />
              {t('actions.activate')}
            </Button>
          ) : null}
          {canRunNow(schedule) ? (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() =>
                runNow.mutate(undefined, {
                  onSuccess: ({ runId }) => onRunQueued(runId),
                  onError: fail('runNow'),
                })
              }
            >
              <Zap aria-hidden />
              {t('actions.runNow')}
            </Button>
          ) : null}
          {canPause(schedule) ? (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => pause.mutate(undefined, { onError: fail('pause') })}
            >
              <Pause aria-hidden />
              {t('actions.pause')}
            </Button>
          ) : null}
          {canResume(schedule) ? (
            <Button
              type="button"
              disabled={pending}
              onClick={() => resume.mutate(undefined, { onError: fail('resume') })}
            >
              <Play aria-hidden />
              {t('actions.resume')}
            </Button>
          ) : null}
          {canEnd(schedule) ? (
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={pending}
              onClick={() => onOpen('end')}
            >
              <Flag aria-hidden />
              {t('actions.end')}
            </Button>
          ) : null}
          {canDelete(schedule) ? (
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={pending}
              onClick={() => onOpen('delete')}
            >
              <Trash2 aria-hidden />
              {t('actions.delete')}
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
